/**
 * useSelectedFiltersStore
 */
import { useCallback } from 'react';

import {
    Queries,
    ApolloLib,
    Mutations,
} from 'bdvp-graphql';

import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';

import { useMedia } from 'bdvp-common/src/hooks';
import { useModalStore } from 'bdvp-common/src/hooks/store';
import { constants as modalConstants } from 'bdvp-graphql/src/resolvers/modals';

import parseQueryInfo from '../../utils/parseQueryInfo';
import overrideFilters from '../../utils/overrideFilters';
import { FILTER_BREAKPOINT } from '../../utils/constants';
import useSearchQueryParam from './useSearchQueryParam';
import useFiltersConfig from '../useFiltersConfig';

const { FILTER_MODAL_NAME } = modalConstants;

const {
    useApolloClient,
    useMutation,
    useQuery,
} = ApolloLib;

const {
    GET_SELECTED_FILTERS,
    GET_QUERY_INFO,
} = Queries;

const {
    BOOTSTRAP_SELECTED_FILTERS,
    REMOVE_SELECTED_FILTER,
    ADD_SELECTED_FILTER,
} = Mutations;

const useSelectedFiltersStore = () => {

    const client = useApolloClient();
    const {
        searchQuery,
        queryType,
        updateSearchQuery,
    } = useSearchQueryParam();

    const { fieldsConfig, loading: isFilterConfigLoading } = useFiltersConfig();

    const isFilterInModal = useMedia(
        [`(min-width: ${BREAKPOINTS[FILTER_BREAKPOINT]})`],
        [false],
        true
    );
    const { isModalOpen } = useModalStore();
    const isFilterModalOpen = isModalOpen(FILTER_MODAL_NAME);

    const { data } = useQuery(GET_SELECTED_FILTERS, {
        skip: isFilterConfigLoading,
    });

    const { selectedFilters } = data || {};

    const [setSelectedFiltersMutation] = useMutation(BOOTSTRAP_SELECTED_FILTERS);
    const [removeSelectedFilterMutation] = useMutation(
        REMOVE_SELECTED_FILTER,
        {
            onCompleted: ({ removeSelectedFilter: data }) => {

                if (isFilterInModal && isFilterModalOpen) {

                    return;

                }

                updateSearchQuery(data);

            },
        }
    );
    const [addSelectedFilterMutation] = useMutation(
        ADD_SELECTED_FILTER,
        {
            onCompleted: ({ addSelectedFilter: data }) => {

                if (isFilterInModal) {

                    return;

                }

                updateSearchQuery(data);

            },
        }
    );

    const setSelectedFilters = useCallback(
        (filters) => (
            setSelectedFiltersMutation({
                variables: {
                    selectedFilters: filters,
                },
            })
        ),
        [setSelectedFiltersMutation]
    );

    const removeSelectedFilter = useCallback(
        (fieldName, value) => (
            removeSelectedFilterMutation({
                variables: {
                    fieldName,
                    value,
                },
            })
        ),
        [removeSelectedFilterMutation]
    );

    const addSelectedFilter = useCallback(
        (filter) => (
            addSelectedFilterMutation({
                variables: {
                    filter,
                },
            })
        ),
        [addSelectedFilterMutation]
    );

    const clearFilters = useCallback(
        () => setSelectedFiltersMutation({ variables: { selectedFilters: [] } }).then(() => {

            if (!isFilterInModal) {

                updateSearchQuery([]);

            };

        }),
        [
            setSelectedFiltersMutation,
            isFilterInModal,
            updateSearchQuery,
        ]
    );

    const setFilters = useCallback(
        (filters) => (
            updateSearchQuery(filters)
        ),
        [updateSearchQuery]
    );

    const applyFilters = useCallback(
        () => {

            const { selectedFilters } = client.readQuery({ query: GET_SELECTED_FILTERS });
            updateSearchQuery(selectedFilters);

        },
        [
            updateSearchQuery,
            client,
        ]
    );

    const resetFilters = useCallback(
        () => {

            if (!searchQuery) {

                setSelectedFiltersMutation({
                    variables: {
                        selectedFilters: [],
                    },
                });

                return;

            };

            const cachedQuery = client.readQuery({
                query: GET_QUERY_INFO,
                variables: {
                    queries: searchQuery,
                    queryType,
                },
            });

            const { filters: cachedFilters } = parseQueryInfo(cachedQuery);

            const filtersOverride = overrideFilters(cachedFilters, fieldsConfig);

            setSelectedFiltersMutation({
                variables: {
                    selectedFilters: filtersOverride,
                },
            });

        },
        [
            searchQuery,
            setSelectedFiltersMutation,
            client,
            queryType,
            fieldsConfig,
        ]
    );

    return {
        selectedFilters,
        setSelectedFilters,
        addSelectedFilter,
        removeSelectedFilter,
        setFilters,
        clearFilters,
        applyFilters,
        resetFilters,
    };

};

export default useSelectedFiltersStore;
